import React from 'react'
import { Interface } from '@ethersproject/abi'
import { BigNumber } from '@ethersproject/bignumber'
import { Currency, CurrencyValue, formatCurrency, StoredCurrency } from '../../../model'
import { StoredTransaction } from '../../../providers/transactions/model'
import { SingleItem } from './SingleItem'
import { SingleOrderItem } from './SingleOrderItem'
import { SingleStakingDepositItem } from './SingleStakingDepositItem'
import { SingleStakingClaimItem } from './SingleStakingClaimItem'
import { SingleStakingWithdrawItem } from './SingleStakingWithdrawItem'
import { SingleTimeReleaseClaimItem } from './SingleTimeReleaseClaimItem'
import { ChainId, INTEGRAL_TOKEN, STAKING_ABI, TIME_RELEASE_ABI } from '../../../constants'
import { decodeEventLogs } from '../../../utils'
import { useEthers } from '../../../hooks'

export interface ItemProps {
  tx: StoredTransaction
  now: Date
}

export function Item({ tx, now }: ItemProps) {
  if (!tx || !tx.txInfo) {
    return <></>
  }
  switch (tx.txInfo.type) {
    case 'APPROVE':
      return <ApproveItem tx={tx} now={now} />
    case 'APPROVE_LIQUIDITY':
      return <ApproveLiquidityItem tx={tx} now={now} />
    case 'SELL':
      return <SellItem tx={tx} now={now} />
    case 'BUY':
      return <BuyItem tx={tx} now={now} />
    case 'DEPOSIT':
      return <DepositItem tx={tx} now={now} />
    case 'WITHDRAW':
      return <WithdrawItem tx={tx} now={now} />
    case 'STAKE':
      return <StakingDepositItem tx={tx} now={now} />
    case 'CLAIM_REWARDS':
      return <StakingClaimItem tx={tx} now={now} />
    case 'WITHDRAW_STAKE':
      return <StakingWithdrawItem tx={tx} now={now} />
    case 'CLAIM':
      return <TimeReleaseClaimItem tx={tx} now={now} />
    default:
      return <></>
  }
}

function ApproveItem({ tx, now }: ItemProps) {
  if (tx.txInfo.type !== 'APPROVE') {
    return <div></div>
  }
  const { symbol } = tx.txInfo
  return <SingleItem now={now} description={`Approve ${symbol}`} submittedAt={tx.submittedAt} symbol0={symbol} />
}

function ApproveLiquidityItem({ tx, now }: ItemProps) {
  if (tx.txInfo.type !== 'APPROVE_LIQUIDITY') {
    return <div></div>
  }
  const { symbol0, symbol1 } = tx.txInfo
  return (
    <SingleItem
      now={now}
      description={`Approve liquidity ${symbol0}-${symbol1}`}
      symbol0={symbol0}
      symbol1={symbol1}
      submittedAt={tx.submittedAt}
    />
  )
}

function BuyItem({ tx, now }: ItemProps) {
  if (tx.txInfo.type !== 'BUY') {
    return <div></div>
  }
  const { currencyIn, currencyOut, expectedValueIn, maxValueIn, valueOut, gasPrepay, slippageTolerance, fees } =
    tx.txInfo
  const { ethRefund, amounts } = tx
  const value0 = getValue0()

  function getValue0() {
    if (amounts && amounts.type === 'BUY') {
      return getValue(currencyIn, amounts.amount0In, amounts.amount1In) || expectedValueIn
    }
    return expectedValueIn
  }

  const { amount0In = undefined, amount1In = undefined } = amounts?.type === 'BUY' ? amounts : {}
  const executed =
    amounts && amounts.type === 'BUY' && amount0In && amount1In && (isNonZero(amount0In) || isNonZero(amount1In))
  return (
    <SingleOrderItem
      now={now}
      description={`Buy ${currencyOut.symbol} with ${currencyIn.symbol}`}
      symbol0={currencyIn.symbol}
      symbol1={currencyOut.symbol}
      value0={`${value0} ${currencyIn.symbol}`}
      value1={`${valueOut} ${currencyOut.symbol}`}
      label0={executed ? 'In' : 'Estimated In'}
      label1={'Out'}
      submittedAt={tx.submittedAt}
      slippageTolerance={slippageTolerance}
      fees={fees}
      gasPrepay={gasPrepay}
      ethRefund={ethRefund}
      moreDetails={{ 'Max In': `${maxValueIn} ${currencyIn.symbol}` }}
    />
  )
}

function SellItem({ tx, now }: ItemProps) {
  if (tx.txInfo.type !== 'SELL') {
    return <div></div>
  }
  const { currencyIn, currencyOut, valueIn, expectedValueOut, minValueOut, gasPrepay, slippageTolerance, fees } =
    tx.txInfo
  const { ethRefund, amounts } = tx
  const value1 = getValue1()

  function getValue1() {
    if (amounts && amounts.type === 'SELL') {
      return getValue(currencyOut, amounts.amount0Out, amounts.amount1Out) || expectedValueOut
    }
    return expectedValueOut
  }

  const { amount0Out = undefined, amount1Out = undefined } = amounts?.type === 'SELL' ? amounts : {}
  const executed =
    amounts && amounts.type === 'SELL' && amount0Out && amount1Out && (isNonZero(amount0Out) || isNonZero(amount1Out))
  return (
    <SingleOrderItem
      now={now}
      description={`Sell ${currencyIn.symbol} for ${currencyOut.symbol}`}
      symbol0={currencyIn.symbol}
      symbol1={currencyOut.symbol}
      value0={`${valueIn} ${currencyIn.symbol}`}
      value1={`${value1} ${currencyOut.symbol}`}
      label0={'In'}
      label1={executed ? 'Out' : 'Estimated Out'}
      submittedAt={tx.submittedAt}
      slippageTolerance={slippageTolerance}
      fees={fees}
      gasPrepay={gasPrepay}
      ethRefund={ethRefund}
      moreDetails={{ 'Min Out': `${minValueOut} ${currencyOut.symbol}` }}
    />
  )
}

function DepositItem({ tx, now }: ItemProps) {
  if (tx.txInfo.type !== 'DEPOSIT') {
    return <div></div>
  }
  const { currency0, currency1, value0, value1, expectedLiquidity, gasPrepay, slippageTolerance, fees } = tx.txInfo
  const { ethRefund, amounts } = tx
  const {
    amount0 = undefined,
    amount1 = undefined,
    liquidityOut = undefined,
  } = amounts?.type === 'DEPOSIT' ? amounts : {}
  const valueIn =
    amount0 && amount1 && (isNonZero(amount0) || isNonZero(amount1))
      ? `${showValue(currency0, amount0)} + ${showValue(currency1, amount1)}`
      : `${value0} ${currency0.symbol} + ${value1} ${currency1.symbol}`

  const executed =
    amounts && amounts.type === 'DEPOSIT' && amount0 && amount1 && (isNonZero(amount0) || isNonZero(amount1))
  return (
    <SingleOrderItem
      now={now}
      description={`Deposit ${currency0.symbol} and ${currency1.symbol}`}
      symbol0={currency0.symbol}
      symbol1={currency1.symbol}
      value0={valueIn}
      value1={`${
        liquidityOut && isNonZero(liquidityOut) ? formatCurrency(18, liquidityOut) : expectedLiquidity
      } LP Tokens`}
      label0={'In'}
      label1={executed ? 'Out' : 'Estimated Out'}
      submittedAt={tx.submittedAt}
      slippageTolerance={slippageTolerance}
      fees={fees}
      gasPrepay={gasPrepay}
      ethRefund={ethRefund}
    />
  )
}

function WithdrawItem({ tx, now }: ItemProps) {
  if (tx.txInfo.type !== 'WITHDRAW') {
    return <div></div>
  }
  const {
    currency0,
    currency1,
    liquidityIn,
    gasPrepay,
    expectedValue0Out,
    expectedValue1Out,
    slippageTolerance,
    fees,
  } = tx.txInfo
  const { ethRefund, amounts } = tx
  const { amount0Out = undefined, amount1Out = undefined } = amounts?.type === 'WITHDRAW' ? amounts : {}
  const [value0Out, value1Out] =
    amount0Out && amount1Out && (isNonZero(amount0Out) || isNonZero(amount1Out))
      ? [showValue(currency0, amount0Out), showValue(currency1, amount1Out)]
      : [`${expectedValue0Out} ${currency0.symbol}`, `${expectedValue1Out} ${currency1.symbol}`]
  const valueOut = `${value0Out} + ${value1Out}`

  const executed =
    amounts &&
    amounts.type === 'WITHDRAW' &&
    amount0Out &&
    amount1Out &&
    (isNonZero(amount0Out) || isNonZero(amount1Out))
  return (
    <SingleOrderItem
      now={now}
      description={`Withdraw ${currency0.symbol} and ${currency1.symbol}`}
      symbol0={currency0.symbol}
      symbol1={currency1.symbol}
      value0={`${liquidityIn} LP Tokens`}
      value1={valueOut}
      label0={'In'}
      label1={executed ? 'Out' : 'Estimated Out'}
      submittedAt={tx.submittedAt}
      slippageTolerance={slippageTolerance}
      fees={fees}
      gasPrepay={gasPrepay}
      ethRefund={ethRefund}
    />
  )
}

function StakingDepositItem({ tx, now }: ItemProps) {
  if (tx.txInfo.type !== 'STAKE') {
    return <div></div>
  }
  return <SingleStakingDepositItem now={now} submittedAt={tx.submittedAt} txInfo={tx.txInfo} />
}

function StakingClaimItem({ tx, now }: ItemProps) {
  const { chainId = ChainId.Mainnet } = useEthers()
  if (tx.txInfo.type !== 'CLAIM_REWARDS') {
    return <div></div>
  }
  const integralToken = INTEGRAL_TOKEN[chainId]

  return (
    <SingleStakingClaimItem
      now={now}
      submittedAt={tx.submittedAt}
      txInfo={tx.txInfo}
      symbol={integralToken.symbol}
      claimedAmount={getClaimedAmountFromStaking(tx, integralToken)}
    />
  )
}

function StakingWithdrawItem({ tx, now }: ItemProps) {
  const { chainId = ChainId.Mainnet } = useEthers()
  if (tx.txInfo.type !== 'WITHDRAW_STAKE') {
    return <div></div>
  }
  const integralToken = INTEGRAL_TOKEN[chainId]

  return (
    <SingleStakingWithdrawItem
      now={now}
      submittedAt={tx.submittedAt}
      txInfo={tx.txInfo}
      symbol={integralToken.symbol}
      withdrawnAmount={getWithdrawnAmountFromStaking(tx, integralToken)}
    />
  )
}

function TimeReleaseClaimItem({ tx, now }: ItemProps) {
  const { chainId = ChainId.Mainnet } = useEthers()
  if (tx.txInfo.type !== 'CLAIM') {
    return <div></div>
  }
  const integralToken = INTEGRAL_TOKEN[chainId]
  const { option1Amount, option2Amount } = getClaimedAmountsFromTimeRelease(tx, integralToken) || {}
  return (
    <SingleTimeReleaseClaimItem
      now={now}
      submittedAt={tx.submittedAt}
      txInfo={tx.txInfo}
      option1ClaimedAmount={option1Amount}
      option2ClaimedAmount={option2Amount}
    />
  )
}

function getClaimedAmountFromStaking(tx: StoredTransaction, integralToken: Currency) {
  if (!tx.receipt) {
    return
  }
  const coder = new Interface(STAKING_ABI)
  const decodedLogs = decodeEventLogs(tx.receipt.logs || [], 'ClaimAll', coder)
  if (decodedLogs.length === 0 || !decodedLogs[0]) {
    return
  }
  const claimedAmount = decodedLogs[0].amount as BigNumber
  return new CurrencyValue(integralToken, claimedAmount)
}

function getWithdrawnAmountFromStaking(tx: StoredTransaction, integralToken: Currency) {
  if (!tx.receipt) {
    return
  }
  const coder = new Interface(STAKING_ABI)
  const decodedLogs = decodeEventLogs(tx.receipt.logs || [], 'WithdrawAll', coder)
  if (decodedLogs.length === 0 || !decodedLogs[0]) {
    return
  }
  const withdrawnAmount = decodedLogs[0].amount as BigNumber
  return new CurrencyValue(integralToken, withdrawnAmount)
}

function getClaimedAmountsFromTimeRelease(tx: StoredTransaction, integralToken: Currency) {
  if (!tx.receipt) {
    return
  }
  const coder = new Interface(TIME_RELEASE_ABI)
  const decodedLogs = decodeEventLogs(tx.receipt.logs || [], 'Claim', coder)
  if (decodedLogs.length === 0 || !decodedLogs[0]) {
    return
  }
  const option1Amount = decodedLogs[0].option1Amount as BigNumber
  const option2Amount = decodedLogs[0].option2Amount as BigNumber
  return {
    option1Amount: new CurrencyValue(integralToken, option1Amount),
    option2Amount: new CurrencyValue(integralToken, option2Amount),
  }
}

function showValue(currency: StoredCurrency, value: string) {
  return `${formatCurrency(currency.decimals, value)} ${currency.symbol}`
}

function getValue(currency: StoredCurrency, amount0?: string, amount1?: string) {
  if (amount0 && isNonZero(amount0)) {
    return formatCurrency(currency.decimals, amount0)
  } else if (amount1 && isNonZero(amount1)) {
    return formatCurrency(currency.decimals, amount1)
  }
}

function isNonZero(amount: string) {
  return !BigNumber.from(amount).isZero()
}
